import { Fragment } from 'react';
import { ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  /**
   * Where the crumb goes. Left off the last one on purpose — the page you are
   * already standing on is not a link, and drawing it as one is how people end
   * up clicking to reload the screen they can already see.
   */
  to?: string;
}

export interface BreadcrumbProps {
  items: Crumb[];
  className?: string;
}

/**
 * The trail above a screen that was opened from a list.
 *
 * A real `<nav><ol>`, not a row of links with slashes between them: a screen
 * reader announces a list of four with "Exam Marks" as item one, which is the
 * whole point of a breadcrumb. `aria-current="page"` marks where you are.
 *
 * Written in tokens so it reads correctly in both themes with no `dark:`
 * prefix — three institute screens still carry their own inline copy in
 * hard-coded slate, and each is one substitution away from using this instead
 * (CLAUDE.md §3).
 */
export function Breadcrumb({ items, className }: BreadcrumbProps) {
  return (
    <nav aria-label="Breadcrumb" className={cn('min-w-0', className)}>
      <ol className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="min-w-0">
                {item.to && !isLast ? (
                  <Link
                    to={item.to}
                    className="truncate text-ink-3 transition-colors hover:text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line rounded-field"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    className={cn('block truncate', isLast ? 'font-bold text-ink' : 'text-ink-3')}
                  >
                    {item.label}
                  </span>
                )}
              </li>

              {!isLast && (
                <li aria-hidden className="text-ink-3">
                  <ChevronRight className="h-3.5 w-3.5" />
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
